import { useEffect, useRef, useState } from 'react';
import type { WorldSource } from '../data/WorldProvider';
import type { WorldView } from '../data/types';
import type { Aim, PlayLimits, PlaySessionView, SteeringPlan } from '../data/play/types';
import { planSteering, steeringReady } from '../data/play/planner';
import { PLAY_SESSIONS_ENABLED } from '../data/api/config';
const min = (a: bigint, b: bigint) => a < b ? a : b;
export function useSteeringPlay(view: WorldView, source: WorldSource) {
  const [aim, setAim] = useState<Aim | null>(null);
  const [plan, setPlan] = useState<SteeringPlan | null>(null);
  const [session, setSession] = useState<PlaySessionView | null>(null);
  const [support, setSupport] = useState({ supported: false, reason: 'Checking session support…' });
  const [busy, setBusy] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [message, setMessage] = useState('');
  const [autoRun, setAutoRun] = useState<number | null>(null);
  const [actionLimit, setActionLimit] = useState(1);
  const [settingsRequest, setSettingsRequest] = useState(0);
  const latest = useRef({ view, source, aim, session, actionLimit }); latest.current = { view, source, aim, session, actionLimit };
  const lock = useRef(false), revision = useRef(0), previewLock = useRef(false), attempted = useRef('');
  const auto = useRef(autoRun); auto.current = autoRun;
  const stopAuto = () => { auto.current = null; setAutoRun(null); };

  useEffect(() => {
    revision.current++; setPlan(null); setSession(null); stopAuto();
    let cancelled = false;
    if (PLAY_SESSIONS_ENABLED) {
      void source.playSupport?.().then(s => { if (!cancelled) setSupport(s); }).catch(() => { if (!cancelled) setSupport({ supported: false, reason: 'Wallet review is available; sessions are currently unavailable.' }); });
      void source.playStatus?.().then(s => { if (!cancelled) setSession(s); }).catch(() => {});
    }
    return () => { cancelled = true; revision.current++; auto.current = null; };
  }, [source, view.user.address, view.user.walletChainId, view.quote.address]);

  const capsFor = (v: WorldView, s: PlaySessionView | null, limit: number) => {
    const buy = BigInt(limit) * 10n ** BigInt(v.quote.decimals);
    // Display price only sets a conservative token cap; actual execution is always sized using pool quotes.
    const wholeTokens = Math.floor(limit / v.market.price);
    const rawSell = Number.isSafeInteger(wholeTokens) && wholeTokens > 0 ? BigInt(wholeTokens) * 10n ** BigInt(v.runner.decimals) : 0n;
    return { buy: min(v.user.connected ? v.user.quote : buy, s?.active ? min(s.maxBuy, s.buyRemaining) : buy), sell: min(v.user.runner, s?.active ? min(s.maxSell, s.sellRemaining) : rawSell) };
  };
  const compute = async (target: Aim) => {
    const { source: src, view: v, session: s, actionLimit: limit } = latest.current;
    if (!src.steeringConfig) throw new Error('Steering preview is unavailable.');
    const config = await src.steeringConfig();
    return planSteering(v, target, config, capsFor(v, s, limit), src);
  };
  const refresh = async () => {
    const { aim: target, view: v, source: src } = latest.current;
    if (!target || previewLock.current || lock.current) return;
    if (!steeringReady(v)) { setPlan(null); return; }
    const rev = revision.current;
    previewLock.current = true; setPlanning(true);
    try {
      const p = await compute(target);
      if (rev === revision.current && p.window === latest.current.view.openInput?.window && p.runId === latest.current.view.run?.id) { setPlan(p); }
      const status = PLAY_SESSIONS_ENABLED ? await src.playStatus?.() : null;
      if (rev === revision.current && status) setSession(status);
    } catch (e) { if (rev === revision.current) { setPlan(null); setMessage((e as Error).message); } }
    finally { previewLock.current = false; setPlanning(false); }
  };
  useEffect(() => { void refresh(); const t = setInterval(() => void refresh(), 2500); return () => clearInterval(t); }, [aim, source, actionLimit, session?.id]);

  const execute = async (target: Aim, automatic = false) => {
    if (lock.current) return;
    const { view: start, source: src } = latest.current;
    const id = `${start.run?.id}:${start.openInput?.window}`;
    if (attempted.current === id) { if (!automatic) setMessage('One action per window. Wait for the next step.'); return; }
    lock.current = true; setBusy(true);
    const rev = revision.current;
    try {
      const p = await compute(target);
      if (rev !== revision.current || (automatic && auto.current !== start.run?.id)) return;
      const v = src.getView();
      if (!steeringReady(v) || p.window !== v.openInput?.window || p.runId !== v.run?.id || (automatic && document.hidden)) throw new Error('The steering window changed. Preview the next step.');
      setPlan(p);
      if (!p.req || !p.quote) { attempted.current = id; setMessage(p.message); if (automatic && p.limited) stopAuto(); return; }
      if (automatic && !latest.current.session?.active) throw new Error('The session has ended.');
      attempted.current = id;
      const tx = PLAY_SESSIONS_ENABLED && latest.current.session?.active && src.playTrade
        ? await src.playTrade(p.req, p.quote, p.window)
        : await src.trade(p.req, p.quote);
      setMessage(tx.stage === 'confirmed' ? 'Trade confirmed. Watch the next step.' : `${tx.stage}: ${tx.detail ?? 'Action not confirmed.'}`);
      if (tx.stage !== 'confirmed') stopAuto();
      const status = PLAY_SESSIONS_ENABLED ? await src.playStatus?.() : null;
      if (rev === revision.current) setSession(status ?? null);
    } catch (e) { setMessage((e as Error).message); stopAuto(); }
    finally { lock.current = false; setBusy(false); }
  };
  const select = (target: Aim, nudge = false) => {
    revision.current++; stopAuto(); setAim(target); setPlan(null); setMessage('');
    latest.current.aim = target;
    if (PLAY_SESSIONS_ENABLED && nudge && session?.active) void execute(target);
  };
  useEffect(() => {
    const onHidden = () => { if (document.hidden) stopAuto(); };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, []);
  useEffect(() => {
    if (!PLAY_SESSIONS_ENABLED || autoRun === null) return;
    const tick = () => {
      const { view: v, aim: target, session: s } = latest.current;
      if (!target || !s?.active || v.run?.id !== auto.current || v.timing.now >= s.expiresAt || document.hidden || v.freshness.stale || v.phase !== 'running') { stopAuto(); return; }
      if (!steeringReady(v)) return;
      void execute(target, true);
    };
    const t = setInterval(tick, 1000); tick();
    return () => clearInterval(t);
  }, [autoRun]);

  const startSession = async (limits: PlayLimits) => {
    if (!PLAY_SESSIONS_ENABLED || lock.current || !source.startPlay) return;
    lock.current = true; setBusy(true); stopAuto();
    const rev = revision.current;
    try {
      const s = await source.startPlay(limits);
      if (rev !== revision.current) return;
      setSession(s); latest.current.session = s; attempted.current = ''; setMessage('Session ready. Tap a direction to steer, or explicitly start autopilot.');
    } catch (e) { setMessage((e as Error).message); }
    finally { lock.current = false; setBusy(false); }
  };
  const endSession = async () => {
    if (!PLAY_SESSIONS_ENABLED) return;
    stopAuto(); revision.current++; setSession(s => s ? { ...s, active: false } : s); setBusy(true);
    try { await source.stopPlay?.(); setSession(await source.playStatus?.() ?? null); setMessage('Session ended.'); }
    catch (e) { setMessage((e as Error).message); }
    finally { setBusy(false); }
  };
  useEffect(() => { setAim(null); setPlan(null); }, [source]);
  const currentPlan = plan && plan.window === view.openInput?.window && plan.runId === view.run?.id && steeringReady(view) && Date.now() - plan.createdAt < 10_000 && (plan.req?.validUntil === undefined || plan.req.validUntil > view.timing.now) ? plan : null;
  return { aim, select, close: () => { revision.current++; stopAuto(); setAim(null); setPlan(null); }, plan: currentPlan, session, support, busy, planning, message, actionLimit, setActionLimit,
    settingsRequest, configureSession: () => { if (!aim) select({ kind: 'right' }); setSettingsRequest(n => n + 1); },
    sessionsEnabled: PLAY_SESSIONS_ENABLED,
    checkSupport: async () => { if (!PLAY_SESSIONS_ENABLED) return; try { const s = await source.playSupport?.(); if (s) setSupport(s); } catch {} },
    startSession, endSession, autoRun, stopAuto, startAuto: () => { if (PLAY_SESSIONS_ENABLED && session?.active && aim && steeringReady(view)) { auto.current = view.run!.id; setAutoRun(view.run!.id); } },
    execute: () => aim && void execute(aim), caps: capsFor(view, session, actionLimit) };
}
export type SteeringPlay = ReturnType<typeof useSteeringPlay>;
